import { useState, useEffect } from "react";
import axios from "axios";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../../Axios";

export default function ManageHigherEducation() {
  const [higherEducation, setHigherEducation] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch higher education data from API
  const fetchHigherEducation = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/higherEducation`);
      setHigherEducation(response.data); // Assuming the response directly returns an array of entries
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch higher education data.");
    }
  };

  useEffect(() => {
    fetchHigherEducation(); // Fetch data when the component mounts
  }, []);

  // Handle view button
  const handleViewClick = (entry) => {
    setSelectedEntry(entry);
    setShowModal(true);
    setIsEditing(false);
  };

  // Handle delete entry
  const handleDelete = async (_id) => {
    try {
      await api.delete(`/api/v1/higherEducation/${_id}`);
      setHigherEducation(higherEducation.filter((entry) => entry._id !== _id));
      toast.success("Entry deleted successfully!");
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete entry.");
    }
  };

  // Handle edit button
  const handleEdit = (entry) => {
    setSelectedEntry({ ...entry });
    setIsEditing(true);
    setShowModal(true);
  };

  // Handle update entry
  const handleUpdateEntry = async () => {
    if (!selectedEntry.name || selectedEntry.name.trim() === "") {
      toast.error("Name is required to update entry!");
      return;
    }

    try {
      // Send PUT request to update the entry
      const response = await api.put(
        `/api/v1/higherEducation/${selectedEntry._id}`,
        selectedEntry // Send selectedEntry directly as JSON
      );

      // On success, fetch the updated higher education list
      fetchHigherEducation();

      // Close modal and show success message
      closeModal();
      toast.success("Entry updated successfully!");
    } catch (error) {
      console.error("Error updating entry:", error);
      toast.error("Failed to update entry.");
    }
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setSelectedEntry((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedEntry(null);
    setIsEditing(false);
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-8">
      <ToastContainer position="top-center" autoClose={3000} />

      <div className="bg-[#00BFA5] text-black text-center font-bold py-4 px-6 max-w-4xl mx-auto text-xl sm:text-2xl md:text-3xl rounded-lg shadow-lg">
        Higher Education Directory - Explore Profiles & Manage Entries
      </div>

      <div className="overflow-x-auto bg-white shadow-lg rounded-lg border-t-4 border-teal-500">
        <table className="min-w-full table-auto border-collapse relative">
          <thead className="bg-[#00BFA5] text-white">
            <tr>
              <th className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">S No.</th>
              <th className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">Name</th>
              <th className="hidden sm:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">University</th>
              <th className="hidden sm:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">Year</th>
              <th className="hidden sm:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">Course</th>
              <th className="px-2 py-3 text-center text-xs sm:text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {higherEducation?.map((entry, index) => (
              <tr key={entry._id} className="border-b hover:bg-teal-50 transition duration-200 ease-in-out">
                <td className="px-2 py-3 text-xs sm:text-sm text-gray-700 font-medium">{index + 1}</td>
                <td className="px-2 py-3 text-xs sm:text-sm text-gray-700 font-medium">{entry.name || "-"}</td>
                <td className="hidden sm:table-cell px-2 py-3 text-xs sm:text-sm">{entry.university || "-"}</td>
                <td className="hidden sm:table-cell px-2 py-3 text-xs sm:text-sm">{entry.year || "-"}</td>
                <td className="hidden sm:table-cell px-2 py-3 text-xs sm:text-sm">{entry.course || "-"}</td>
                <td className="px-2 py-3 text-center space-x-1 sm:space-x-3">
                  <button
                    onClick={() => handleViewClick(entry)}
                    className="text-blue-500 hover:text-blue-700 transition duration-300"
                    title="View Profile"
                  >
                    <FaEye size={16} />
                  </button>
                  <button
                    onClick={() => handleEdit(entry)}
                    className="text-teal-500 hover:text-teal-700 transition duration-300"
                    title="Edit Profile"
                  >
                    <FaEdit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(entry._id)}
                    className="text-red-500 hover:text-red-700 transition duration-300"
                    title="Delete Profile"
                  >
                    <FaTrash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-1/2 p-6 space-y-6">
            <h2 className="text-xl font-bold text-teal-500">{isEditing ? "Edit Higher Education Profile" : "View Higher Education Profile"}</h2>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-semibold">Name</label>
                <input
                  type="text"
                  value={selectedEntry?.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  disabled={!isEditing}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold">University</label>
                <input
                  type="text"
                  value={selectedEntry?.university || ""}
                  onChange={(e) => handleInputChange("university", e.target.value)}
                  disabled={!isEditing}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold">Year</label>
                <input
                  type="text"
                  value={selectedEntry?.year || ""}
                  onChange={(e) => handleInputChange("year", e.target.value)}
                  disabled={!isEditing}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold">Course</label>
                <input
                  type="text"
                  value={selectedEntry?.course || ""}
                  onChange={(e) => handleInputChange("course", e.target.value)}
                  disabled={!isEditing}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button onClick={closeModal} className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md">
                Close
              </button>
              {isEditing && (
                <button onClick={handleUpdateEntry} className="bg-teal-500 text-white px-4 py-2 rounded-md">
                  Update Entry
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
